import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import authService from '../../utils/authService';
import '../../styles/BlogEditor.css';

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState('pt');
  const [post, setPost] = useState({
    title: { pt: '', en: '' },
    excerpt: { pt: '', en: '' },
    content: { pt: '', en: '' },
    category: 'other',
    tags: [],
    status: 'draft',
    featured: false,
    featuredImage: null,
    seo: {
      metaTitle: { pt: '', en: '' },
      metaDescription: { pt: '', en: '' }
    }
  });
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (id) fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const token = authService.getToken();
      const response = await axios.get(`/api/admin/blog/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPost(response.data.post);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const formData = new FormData();
    formData.append('title_pt', post.title.pt);
    formData.append('title_en', post.title.en);
    formData.append('excerpt_pt', post.excerpt.pt);
    formData.append('excerpt_en', post.excerpt.en);
    formData.append('content_pt', post.content.pt);
    formData.append('content_en', post.content.en);
    formData.append('category', post.category);
    formData.append('tags', JSON.stringify(post.tags));
    formData.append('status', post.status);
    formData.append('featured', post.featured);
    formData.append('seo_title_pt', post.seo.metaTitle.pt);
    formData.append('seo_title_en', post.seo.metaTitle.en);
    formData.append('seo_desc_pt', post.seo.metaDescription.pt);
    formData.append('seo_desc_en', post.seo.metaDescription.en);
    if (imageFile) formData.append('featuredImage', imageFile);

    try {
      const token = authService.getToken();
      const url = id ? `/api/admin/blog/${id}` : '/api/admin/blog';
      const method = id ? 'put' : 'post';
      
      await axios[method](url, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      navigate('/admin/blog');
    } catch (error) {
      alert('Failed to save post');
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput && !post.tags.includes(tagInput.toLowerCase())) {
      setPost({ ...post, tags: [...post.tags, tagInput.toLowerCase()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setPost({ ...post, tags: post.tags.filter(t => t !== tag) });
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="blog-editor">
      <div className="editor-header">
        <h1>{id ? 'Edit Post' : 'New Post'}</h1>
        <div className="lang-tabs">
          <button className={lang === 'pt' ? 'active' : ''} onClick={() => setLang('pt')}>PT</button>
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Title */}
        <div className="form-group">
          <label>Title ({lang.toUpperCase()})</label>
          <input
            type="text"
            value={post.title[lang]}
            onChange={(e) => setPost({ ...post, title: { ...post.title, [lang]: e.target.value } })}
            required
          />
        </div>

        {/* Excerpt */}
        <div className="form-group">
          <label>Excerpt ({lang.toUpperCase()})</label>
          <textarea
            value={post.excerpt[lang]}
            onChange={(e) => setPost({ ...post, excerpt: { ...post.excerpt, [lang]: e.target.value } })}
            rows="3"
            required
          />
        </div>

        {/* Content */}
        <div className="form-group">
          <label>Content ({lang.toUpperCase()})</label>
          <textarea
            value={post.content[lang]}
            onChange={(e) => setPost({ ...post, content: { ...post.content, [lang]: e.target.value } })}
            rows="15"
            required
          />
        </div>

        {/* Meta */}
        <div className="form-row">
          <div className="form-group">
            <label>Category</label>
            <select value={post.category} onChange={(e) => setPost({ ...post, category: e.target.value })}>
              <option value="tarot">Tarot</option>
              <option value="astrology">Astrology</option>
              <option value="spirituality">Spirituality</option>
              <option value="wellness">Wellness</option>
              <option value="tips">Tips</option>
              <option value="news">News</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select value={post.status} onChange={(e) => setPost({ ...post, status: e.target.value })}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Tags */}
        <div className="form-group">
          <label>Tags</label>
          <div className="tags-input">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Add tag..."
            />
            <button type="button" onClick={addTag}>+</button>
          </div>
          <div className="tags-list">
            {post.tags.map(tag => (
              <span key={tag} className="tag">
                {tag}
                <button type="button" onClick={() => removeTag(tag)}>×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Image */}
        <div className="form-group">
          <label>Featured Image</label>
          <input type="file" accept="image/jpeg,image/png" onChange={(e) => setImageFile(e.target.files[0])} />
          {post.featuredImage && <img src={post.featuredImage} alt="Preview" className="image-preview" />}
        </div>

        {/* SEO */}
        <details className="seo-section">
          <summary>SEO Settings</summary>
          <div className="form-group">
            <label>Meta Title ({lang.toUpperCase()})</label>
            <input
              type="text"
              value={post.seo.metaTitle[lang]}
              onChange={(e) => setPost({ ...post, seo: { ...post.seo, metaTitle: { ...post.seo.metaTitle, [lang]: e.target.value } } })}
            />
          </div>
          <div className="form-group">
            <label>Meta Description ({lang.toUpperCase()})</label>
            <textarea
              value={post.seo.metaDescription[lang]}
              onChange={(e) => setPost({ ...post, seo: { ...post.seo, metaDescription: { ...post.seo.metaDescription, [lang]: e.target.value } } })}
              rows="2"
            />
          </div>
        </details>

        {/* Featured */}
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={post.featured}
              onChange={(e) => setPost({ ...post, featured: e.target.checked })}
            />
            Featured Post
          </label>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <button type="button" onClick={() => navigate('/admin/blog')} className="btn btn-secondary">Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : id ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogEditor;
